import {forwardRef} from 'react';
import {Autocomplete, CircularProgress, TextField} from '@mui/material';

import {LeagueSeasonDTO} from '../../api/types';
import {useGetLeagueSeasons} from '../../apiHooks';

type Props = {
  value: string | null;
  onChange: (value: string | null) => void;
  categorySlug?: string;
  // The internal league UUID, not the lsports competition id.
  leagueId?: string;
  label?: string;
  disabled?: boolean;
};

const getSeasonLabel = (season: LeagueSeasonDTO) =>
  `${season.season} (${season.start} - ${season.end})`;

export const SeasonSelect = forwardRef<HTMLInputElement, Props>(
  (
    {value, onChange, categorySlug, leagueId, label = 'Season', disabled},
    ref,
  ) => {
    const {data: seasons = [], isPending} = useGetLeagueSeasons(
      categorySlug,
      leagueId,
    );

    const selected = seasons.find(s => s.season === value) ?? null;
    // Keep a season that is not in the list (or arrived before it) selectable
    // so the field never renders as empty while the request resolves.
    const options =
      value && !selected
        ? [{season: value, start: '', end: ''}, ...seasons]
        : seasons;

    return (
      <Autocomplete
        ref={ref}
        value={selected ?? (value ? {season: value, start: '', end: ''} : null)}
        options={options}
        loading={isPending}
        disabled={disabled}
        getOptionLabel={option =>
          option.start ? getSeasonLabel(option) : option.season
        }
        isOptionEqualToValue={(opt, val) => opt.season === val.season}
        onChange={(_, newValue) => onChange(newValue?.season ?? null)}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isPending && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    );
  },
);
